import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import type { ServiceOffering } from '@freelancer/datastore/collections';
import { PipesModule } from '@freelancer/pipes';
import { TrackingModule } from '@freelancer/tracking';
import { ModalService, UiModule } from '@freelancer/ui';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ModalColor } from '@freelancer/ui/modal';
import {
  PictureLayout,
  PictureLoadMethod,
  PictureObjectFit,
} from '@freelancer/ui/picture';
import { isDefined } from '@freelancer/utils';
import { filter, map, Observable, shareReplay } from 'rxjs';
import { Service } from '../ai-services/ai-services.types';

@Component({
  standalone: true,
  imports: [PipesModule, TrackingModule, UiModule],
  selector: 'app-ai-service-item',
  template: `
    <div class="Content">
      <div>
        <div class="Header mb-xsmall tablet:mb-small">
          <fl-icon
            class="mr-xsmall"
            [color]="IconColor.PRIMARY"
            [name]="service.headerIcon"
            [fillIconFont]="false"
            [useIconFont]="true"
            [size]="IconSize.MID"
          ></fl-icon>
          <p class="text-light text-body-24 tablet:text-body-24 font-bold">
            {{ service.header }}
          </p>
        </div>

        <p
          class="text-light text-body-16 tablet:text-body-16 mb-small tablet:mb-large"
        >
          {{ service.description }}
        </p>
        <div
          class="ImageContainer"
          flTrackingLabel="VideoDemoPlayed"
          (click)="handleClickVideoPreview()"
        >
          <fl-picture
            class="Image"
            [height]="800"
            [width]="1000"
            [layout]="PictureLayout.RESPONSIVE"
            [loadMethod]="PictureLoadMethod.LAZY"
            [objectFit]="PictureObjectFit.COVER"
            [src]="service.src"
          ></fl-picture>
          <div
            class="IconTextContainer"
            [class.IconTextContainer-homePage]="isHomePage"
          >
            <fl-picture
              class="ImageIcon mb-xxsmall tablet:mb-small desktop:mb-xxsmall"
              [height]="57"
              [width]="57"
              [layout]="PictureLayout.RESPONSIVE"
              [loadMethod]="PictureLoadMethod.LAZY"
              [src]="'back-to-work/ai-services/v2/play_circle.svg'"
            ></fl-picture>
            <p
              class="text-light text-body-16 tablet:text-body-24 desktop:text-body-12 desktop_large:text-body-16 font-black text-center"
              i18n="Watch a demo"
            >
              Watch a demo
            </p>
          </div>
        </div>
      </div>

      <div>
        @if (isDefined(serviceOfferingDiscountedPrice$ | flAsync)) {
          <p
            class="text-light text-body-16 tablet:text-body-16 text-center font-bold mb-small"
            i18n="Consultation price button text"
          >
            Consultation for only
          </p>
        }
        <fl-button
          class="Button"
          [busy]="!(isServiceOfferingsCollectionReady$ | flAsync)"
          [color]="ButtonColor.PRIMARY"
          [display]="'flex'"
          [flTrackingLabel]="service.type + 'Button'"
          [label]="service.type + 'Button'"
          [link]="service.link"
          [size]="ButtonSize.MID"
          [sizeTablet]="ButtonSize.LARGE"
        >
          @if (serviceOffering$ | flAsync; as serviceOffering) {
            <div class="Button-text">
              @if (!isDefined(serviceOfferingDiscountedPrice$ | flAsync)) {
                <p
                  class="text-color-inherit text-body-16 text-right font-weight-inherit mr-xxsmall tablet:mr-xxsmall"
                  i18n="Consultation price button text"
                >
                  Consultation for only
                </p>
              }
              <p
                class="text-color-inherit text-title-50"
                [class.font-bold]="
                  !isDefined(serviceOfferingDiscountedPrice$ | flAsync)
                "
                [class.line-through]="
                  isDefined(serviceOfferingDiscountedPrice$ | flAsync)
                "
              >
                {{
                  serviceOffering.price
                    | flCurrency
                      : 'USD'
                      : { disableCode: true, withPrecision: false }
                }}
              </p>
              @if (isDefined(serviceOfferingDiscountedPrice$ | flAsync)) {
                <p class="text-color-inherit text-title-50 font-bold">
                  &nbsp;{{
                    serviceOfferingDiscountedPrice$
                      | flAsync
                      | flCurrency
                        : 'USD'
                        : { disableCode: true, withPrecision: false }
                  }}
                </p>
              }
            </div>
          }
        </fl-button>
      </div>
    </div>
  `,
  styleUrls: ['./ai-service-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiServiceItemComponent implements OnInit {
  private modalService = inject(ModalService);

  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  IconColor = IconColor;
  IconSize = IconSize;
  PictureLayout = PictureLayout;
  PictureLoadMethod = PictureLoadMethod;
  PictureObjectFit = PictureObjectFit;

  isDefined = isDefined;

  @Input() service: Service;
  @Input() isHomePage = false;
  @Input() filteredAiServiceOfferings$: Observable<readonly ServiceOffering[]>;
  @Input() isServiceOfferingsCollectionReady$: Observable<boolean>;

  partnerSrc?: string;

  serviceOffering$: Observable<ServiceOffering>;
  serviceOfferingDiscountedPrice$: Observable<number | undefined>;

  ngOnInit(): void {
    this.serviceOffering$ = this.filteredAiServiceOfferings$.pipe(
      map(filteredAiServiceOfferings =>
        filteredAiServiceOfferings.find(
          serviceOffering =>
            serviceOffering.seoUrl &&
            serviceOffering.seoUrl ===
              this.service.link.replace('/service/', ''),
        ),
      ),
      filter(isDefined),
    );

    this.serviceOfferingDiscountedPrice$ = this.serviceOffering$.pipe(
      map(aiActionPlanService => {
        const now = Date.now();
        const hasActiveDiscount = aiActionPlanService.discountDetails
          ? now >= aiActionPlanService.discountDetails.validFrom &&
            now <= aiActionPlanService.discountDetails.validUntil
          : false;

        return hasActiveDiscount
          ? aiActionPlanService.discountDetails?.discountedPrice
          : undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  handleClickVideoPreview(): void {
    this.modalService.open('VideoModal', {
      inputs: {
        src: this.service.videoSrc,
      },
      edgeToEdge: true,
      closeable: true,
      color: ModalColor.DARK,
    });
  }
}
